/**
 * Advanced Haptic Feedback Service for Mobile UX Optimization
 * Provides contextual haptic patterns for different interaction types
 * with accessibility considerations and battery optimization
 */

interface HapticPattern {
  type:
    | "impact"
    | "notification"
    | "selection"
    | "warning"
    | "success"
    | "error"
    | "texture"
    | "rhythm";
  intensity: "light" | "medium" | "heavy";
  duration?: number;
  pattern?: number[];
  repeat?: number;
  delay?: number;
}

interface HapticConfig {
  enabled: boolean;
  intensity: number; // 0.0 - 1.0
  enableForActions: boolean;
  enableForNavigation: boolean;
  enableForFeedback: boolean;
  enableForErrors: boolean;
  respectSystemSettings: boolean;
  batteryOptimization: boolean;
  accessibilityMode: boolean;
}

interface HapticCapabilities {
  hasVibration: boolean;
  hasAdvancedVibration: boolean;
  hasTapticEngine: boolean;
  supportedPatterns: string[];
  maxIntensity: number;
  batteryLevel?: number;
  isLowPowerMode?: boolean;
}

type HapticContextType =
  | "button-press"
  | "component-select"
  | "component-move"
  | "component-resize"
  | "component-delete"
  | "canvas-zoom"
  | "canvas-pan"
  | "gesture-start"
  | "gesture-end"
  | "error-feedback"
  | "success-feedback"
  | "warning-feedback"
  | "navigation"
  | "property-change"
  | "toolbar-action"
  | "long-press"
  | "double-tap"
  | "swipe"
  | "pinch"
  | "rotation"
  | "edge-reached"
  | "snap-to-grid"
  | "conflict-resolution"
  | "sync-complete"
  | "save-complete"
  | "undo-redo"
  | "accessibility-focus";

interface AccessibilitySettings {
  reducedMotion: boolean;
  highContrast: boolean;
  enableHaptics: boolean;
}

class HapticFeedbackService {
  private config: HapticConfig;
  private capabilities: HapticCapabilities;
  private lastHapticTime: number = 0;
  private hapticQueue: HapticPattern[] = [];
  private isProcessing: boolean = false;
  private performanceMode: boolean = false;
  private accessibilitySettings: AccessibilitySettings = {
    reducedMotion: false,
    highContrast: false,
    enableHaptics: true,
  };

  // Predefined haptic patterns for different contexts
  private readonly hapticPatterns: Record<HapticContextType, HapticPattern> = {
    "button-press": {
      type: "impact",
      intensity: "light",
      duration: 10,
    },
    "component-select": {
      type: "selection",
      intensity: "light",
      duration: 15,
    },
    "component-move": {
      type: "texture",
      intensity: "light",
      pattern: [5, 5],
      repeat: 1,
    },
    "component-resize": {
      type: "texture",
      intensity: "medium",
      pattern: [10, 5, 10],
    },
    "component-delete": {
      type: "warning",
      intensity: "heavy",
      pattern: [30, 10, 30],
    },
    "canvas-zoom": {
      type: "texture",
      intensity: "light",
      pattern: [3, 3, 3],
      repeat: 2,
    },
    "canvas-pan": {
      type: "texture",
      intensity: "light",
      pattern: [2],
      repeat: 1,
    },
    "gesture-start": {
      type: "impact",
      intensity: "light",
      duration: 8,
    },
    "gesture-end": {
      type: "impact",
      intensity: "light",
      duration: 12,
    },
    "error-feedback": {
      type: "error",
      intensity: "heavy",
      pattern: [50, 20, 50, 20, 50],
    },
    "success-feedback": {
      type: "success",
      intensity: "medium",
      pattern: [10, 10, 20],
    },
    "warning-feedback": {
      type: "warning",
      intensity: "medium",
      pattern: [25, 15, 25],
    },
    navigation: {
      type: "selection",
      intensity: "light",
      duration: 10,
    },
    "property-change": {
      type: "notification",
      intensity: "light",
      duration: 8,
    },
    "toolbar-action": {
      type: "impact",
      intensity: "light",
      duration: 12,
    },
    "long-press": {
      type: "notification",
      intensity: "medium",
      pattern: [20, 10, 20],
    },
    "double-tap": {
      type: "selection",
      intensity: "medium",
      pattern: [8, 8, 8],
    },
    swipe: {
      type: "texture",
      intensity: "light",
      pattern: [5, 3, 5, 3, 5],
    },
    pinch: {
      type: "texture",
      intensity: "light",
      pattern: [6, 4],
      repeat: 2,
    },
    rotation: {
      type: "texture",
      intensity: "light",
      pattern: [4, 2, 4, 2, 4, 2],
    },
    "edge-reached": {
      type: "warning",
      intensity: "medium",
      pattern: [20, 10, 20],
    },
    "snap-to-grid": {
      type: "notification",
      intensity: "light",
      duration: 6,
    },
    "conflict-resolution": {
      type: "warning",
      intensity: "medium",
      pattern: [15, 10, 15, 10, 15],
    },
    "sync-complete": {
      type: "success",
      intensity: "light",
      pattern: [8, 8, 15],
    },
    "save-complete": {
      type: "success",
      intensity: "medium",
      pattern: [12, 8, 20],
    },
    "undo-redo": {
      type: "notification",
      intensity: "light",
      duration: 10,
    },
    "accessibility-focus": {
      type: "selection",
      intensity: "medium",
      pattern: [15, 10, 15],
    },
  };

  constructor(initialConfig?: Partial<HapticConfig>) {
    this.config = {
      enabled: true,
      intensity: 1.0,
      enableForActions: true,
      enableForNavigation: true,
      enableForFeedback: true,
      enableForErrors: true,
      respectSystemSettings: true,
      batteryOptimization: true,
      accessibilityMode: false,
      ...initialConfig,
    };

    this.capabilities = this.detectCapabilities();
    this.loadAccessibilitySettings();
    this.initializeBatteryMonitoring();
  }

  /**
   * Detect device haptic capabilities
   */
  private detectCapabilities(): HapticCapabilities {
    const hasVibration = "vibrate" in navigator;
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);

    return {
      hasVibration,
      hasAdvancedVibration: hasVibration && (isAndroid || isIOS),
      hasTapticEngine: isIOS && hasVibration,
      supportedPatterns: hasVibration ? ["basic", "pattern", "duration"] : [],
      maxIntensity: isIOS ? 1.0 : 0.8, // iOS has more precise control
    };
  }

  /**
   * Load accessibility settings from system/user preferences
   */
  private loadAccessibilitySettings(): void {
    try {
      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      // Check for high contrast preference
      const prefersHighContrast = window.matchMedia(
        "(prefers-contrast: high)",
      ).matches;

      this.accessibilitySettings = {
        reducedMotion: prefersReducedMotion,
        highContrast: prefersHighContrast,
        enableHaptics: true, // Default to enabled, can be overridden by user
      };

      // Adjust config based on accessibility preferences
      if (prefersReducedMotion) {
        this.config.accessibilityMode = true;
        this.config.intensity *= 0.7; // Reduce intensity for accessibility
      }
    } catch (error) {
      console.warn("Could not load accessibility settings:", error);
    }
  }

  /**
   * Initialize battery monitoring for optimization
   */
  private initializeBatteryMonitoring(): void {
    if (!this.config.batteryOptimization) return;

    try {
      if (navigator.getBattery) {
        navigator.getBattery().then((battery) => {
          this.capabilities.batteryLevel = battery.level;
          this.capabilities.isLowPowerMode = battery.level < 0.2;

          // Listen for battery changes
          battery.addEventListener("levelchange", () => {
            this.capabilities.batteryLevel = battery.level;
            this.capabilities.isLowPowerMode = battery.level < 0.2;
            this.updatePerformanceMode();
          });

          battery.addEventListener("chargingchange", () => {
            this.updatePerformanceMode();
          });

          this.updatePerformanceMode();
        });
      }
    } catch (error) {
      console.warn("Battery API not available:", error);
    }
  }

  /**
   * Update performance mode based on battery and system conditions
   */
  private updatePerformanceMode(): void {
    const { batteryLevel, isLowPowerMode } = this.capabilities;

    this.performanceMode =
      (batteryLevel !== undefined && batteryLevel < 0.3) ||
      isLowPowerMode === true ||
      this.accessibilitySettings.reducedMotion;

    if (this.performanceMode) {
      // Reduce haptic intensity and frequency in performance mode
      this.config.intensity *= 0.5;
    }
  }

  /**
   * Check if haptic feedback should be triggered
   */
  private shouldTriggerHaptic(context: HapticContextType): boolean {
    if (!this.config.enabled || !this.capabilities.hasVibration) return false;
    if (this.performanceMode && !this.isEssentialHaptic(context)) return false;
    if (this.config.accessibilityMode && !this.isAccessibilityRelevant(context))
      return false;

    // Throttle haptics to prevent overwhelming the user
    const now = Date.now();
    const timeSinceLastHaptic = now - this.lastHapticTime;
    const minimumInterval = this.performanceMode ? 100 : 50; // ms

    if (timeSinceLastHaptic < minimumInterval) return false;

    // Check context-specific settings
    if (context.includes("navigation") && !this.config.enableForNavigation)
      return false;
    if (context.includes("error") && !this.config.enableForErrors) return false;
    if (context.includes("feedback") && !this.config.enableForFeedback)
      return false;

    return true;
  }

  /**
   * Check if haptic is essential for accessibility/core functionality
   */
  private isEssentialHaptic(context: HapticContextType): boolean {
    const essentialContexts: HapticContextType[] = [
      "error-feedback",
      "warning-feedback",
      "accessibility-focus",
      "component-delete",
      "conflict-resolution",
    ];
    return essentialContexts.includes(context);
  }

  /**
   * Check if haptic is relevant for accessibility users
   */
  private isAccessibilityRelevant(context: HapticContextType): boolean {
    const accessibilityContexts: HapticContextType[] = [
      "accessibility-focus",
      "component-select",
      "navigation",
      "error-feedback",
      "success-feedback",
      "warning-feedback",
      "button-press",
    ];
    return accessibilityContexts.includes(context);
  }

  /**
   * Process haptic pattern and trigger vibration
   */
  private async processHapticPattern(pattern: HapticPattern): Promise<void> {
    if (!this.canUseVibration()) return;

    try {
      const adjustedIntensity =
        pattern.intensity === "light"
          ? 0.3
          : pattern.intensity === "medium"
            ? 0.6
            : 1.0;

      const finalIntensity = Math.min(
        adjustedIntensity * this.config.intensity,
        this.capabilities.maxIntensity,
      );

      if (pattern.pattern) {
        // Use pattern-based vibration
        const adjustedPattern = pattern.pattern.map((duration) =>
          Math.round(duration * finalIntensity),
        );

        const repeatCount = pattern.repeat || 1;
        for (let i = 0; i < repeatCount; i++) {
          navigator.vibrate(adjustedPattern);
          if (i < repeatCount - 1 && pattern.delay) {
            await new Promise((resolve) => setTimeout(resolve, pattern.delay));
          }
        }
      } else if (pattern.duration) {
        // Use duration-based vibration
        const adjustedDuration = Math.round(pattern.duration * finalIntensity);
        navigator.vibrate(adjustedDuration);
      } else {
        // Default short vibration
        navigator.vibrate(Math.round(10 * finalIntensity));
      }

      this.lastHapticTime = Date.now();
    } catch (error) {
      console.warn("Failed to trigger haptic feedback:", error);
    }
  }

  private canUseVibration(): boolean {
    if (!this.capabilities.hasVibration) return false;

    const activation = navigator.userActivation;
    if (!activation) return true;

    return activation.isActive || activation.hasBeenActive;
  }

  /**
   * Add haptic to queue for processing
   */
  private async enqueueHaptic(pattern: HapticPattern): Promise<void> {
    this.hapticQueue.push(pattern);

    if (!this.isProcessing) {
      this.isProcessing = true;
      await this.processHapticQueue();
      this.isProcessing = false;
    }
  }

  /**
   * Process queued haptic patterns
   */
  private async processHapticQueue(): Promise<void> {
    while (this.hapticQueue.length > 0) {
      const pattern = this.hapticQueue.shift();
      if (pattern) {
        await this.processHapticPattern(pattern);
        // Small delay between patterns to prevent overlap
        await new Promise((resolve) => setTimeout(resolve, 20));
      }
    }
  }

  /**
   * Trigger haptic feedback for a specific context
   */
  public async trigger(
    context: HapticContextType,
    customPattern?: Partial<HapticPattern>,
  ): Promise<void> {
    if (!this.shouldTriggerHaptic(context)) return;

    const basePattern = this.hapticPatterns[context];
    const pattern: HapticPattern = {
      ...basePattern,
      ...customPattern,
    };

    await this.enqueueHaptic(pattern);
  }

  /**
   * Trigger custom haptic pattern
   */
  public async triggerCustom(pattern: HapticPattern): Promise<void> {
    if (!this.config.enabled || !this.capabilities.hasVibration) return;

    await this.enqueueHaptic(pattern);
  }

  /**
   * Update haptic configuration
   */
  public updateConfig(newConfig: Partial<HapticConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  public getConfig(): HapticConfig {
    return { ...this.config };
  }

  /**
   * Get device capabilities
   */
  public getCapabilities(): HapticCapabilities {
    return { ...this.capabilities };
  }

  /**
   * Test haptic feedback with different patterns
   */
  public async test(
    testType: "light" | "medium" | "heavy" | "pattern" = "medium",
  ): Promise<void> {
    const testPatterns: Record<string, HapticPattern> = {
      light: { type: "impact", intensity: "light", duration: 10 },
      medium: { type: "impact", intensity: "medium", duration: 20 },
      heavy: { type: "impact", intensity: "heavy", duration: 30 },
      pattern: {
        type: "rhythm",
        intensity: "medium",
        pattern: [10, 10, 20, 10, 30],
      },
    };

    await this.triggerCustom(testPatterns[testType]);
  }

  /**
   * Enable or disable haptic feedback
   */
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if haptic feedback is available and enabled
   */
  public isAvailable(): boolean {
    return this.capabilities.hasVibration && this.config.enabled;
  }

  /**
   * Clear haptic queue (useful for performance optimization)
   */
  public clearQueue(): void {
    this.hapticQueue = [];
  }

  /**
   * Get performance metrics
   */
  public getMetrics(): {
    isPerformanceMode: boolean;
    batteryLevel?: number;
    queueLength: number;
    lastHapticTime: number;
    accessibilityMode: boolean;
  } {
    return {
      isPerformanceMode: this.performanceMode,
      batteryLevel: this.capabilities.batteryLevel,
      queueLength: this.hapticQueue.length,
      lastHapticTime: this.lastHapticTime,
      accessibilityMode: this.config.accessibilityMode,
    };
  }
}

// Create singleton instance
export const hapticFeedbackService = new HapticFeedbackService();

// Export types for TypeScript support
export type {
  HapticPattern,
  HapticConfig,
  HapticCapabilities,
  HapticContextType,
};

export default hapticFeedbackService;
